import type { GpsShotQuality } from "../../shared/contracts/gps-shot.js";

export interface GpsPoint {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

// Product thresholds for field testing, not a guarantee of physical accuracy.
export const GPS_TARGET_ACCURACY_METERS = 5;
export const GPS_MAX_ACCURACY_METERS = 15;
export const GPS_MAX_POINT_AGE_MS = 3_000;

export function isGpsPoint(value: unknown): value is GpsPoint {
  if (!value || typeof value !== "object") return false;
  const point = value as GpsPoint;
  return (
    Number.isFinite(point.latitude) &&
    Math.abs(point.latitude) <= 90 &&
    Number.isFinite(point.longitude) &&
    Math.abs(point.longitude) <= 180 &&
    Number.isFinite(point.accuracy) &&
    point.accuracy > 0 &&
    Number.isFinite(point.timestamp) &&
    point.timestamp > 0
  );
}

export function isFreshGpsPoint(point: GpsPoint, now: number): boolean {
  return (
    point.timestamp <= now + 1_000 &&
    now - point.timestamp <= GPS_MAX_POINT_AGE_MS
  );
}

export function gpsDistanceMeters(start: GpsPoint, end: GpsPoint): number {
  const radians = (degrees: number) => (degrees * Math.PI) / 180;
  const latitude = radians(end.latitude - start.latitude);
  const longitude = radians(end.longitude - start.longitude);
  const a =
    Math.sin(latitude / 2) ** 2 +
    Math.cos(radians(start.latitude)) *
      Math.cos(radians(end.latitude)) *
      Math.sin(longitude / 2) ** 2;
  return (
    6_371_008.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(Math.max(0, 1 - a)))
  );
}

export function gpsShotQuality(
  distance: number,
  startAccuracy: number,
  endAccuracy: number,
): GpsShotQuality {
  return distance <= startAccuracy + endAccuracy
    ? "short-distance"
    : "approximate";
}

export function formatGpsDistance(meters: number, unit: "m" | "yd"): string {
  return `≈${Math.round(unit === "yd" ? meters / 0.9144 : meters)} ${unit}`;
}
